
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, BookOpen, Zap, TrendingUp, Star } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { TIER_PRICING, TIER_INFO, MembershipTier } from '../types/membership';
import { TierCard } from '../components/TierBadge';
import { usePageMeta } from '../hooks/usePageMeta';

interface GuestStats {
  sessionsCompleted: number;
  wordsRead: number;
  maxWpm: number;
  avgWpm: number;
  timeSaved: number;
}

const UpsellPageOld: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Speed Reading Premium & Reading App Upgrade Plans | Flash-Read',
    description: 'Upgrade to Flash-Read premium for unlimited stories, achievements, streaks, cloud sync, and advanced analytics. Choose a reading app subscription plan — Starter, Reader, or Ultra.',
  });

  const [stats, setStats] = useState<GuestStats>({
    sessionsCompleted: 3,
    wordsRead: 0,
    maxWpm: 0,
    avgWpm: 0,
    timeSaved: 0,
  });

  // Load guest stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('flashread_stats');
    if (savedStats) {
      try {
        const parsed = JSON.parse(savedStats);
        const wordsRead = parsed.wordsRead || 0;
        const maxWpm = parsed.maxWpm || 0;
        const avgWpm = parsed.avgWpm || 0;
        // Estimate time saved: at 200 WPM vs their avg speed
        const timeSavedMinutes = wordsRead > 0 && avgWpm > 0
          ? (wordsRead / 200) - (wordsRead / avgWpm)
          : 0;

        setStats({
          sessionsCompleted: 3,
          wordsRead,
          maxWpm,
          avgWpm,
          timeSaved: Math.round(timeSavedMinutes * 60), // seconds
        });
      } catch {
        // Use defaults
      }
    }
  }, []);

  // Chart data for speed progression
  const speedData = [
    { name: 'Session 1', wpm: 200 },
    { name: 'Session 2', wpm: Math.min(400, stats.avgWpm > 0 ? Math.round(stats.avgWpm * 0.8) : 400) },
    { name: 'Session 3', wpm: stats.avgWpm > 0 ? stats.avgWpm : 500 },
  ];

  const tiers: MembershipTier[] = ['starter', 'reader', 'ultra'];

  return (
    <div className="min-h-screen pt-20 pb-12 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-6 bg-amber-100 dark:bg-amber-900/30">
            <CheckCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-6">
            You've read your <br className="hidden md:block"/>
            <span className="text-primary">3 free stories.</span>
          </h1>
          
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Here's what you accomplished — and what's waiting when you sign up.
          </p>
        </div>

        {/* Stats & Validation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          
          {/* Chart Card */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-primary" />
                Your Speed Journey
              </h3>
              <span className="text-xs font-semibold uppercase text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">3 Sessions</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={speedData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} dy={10} />
                  <YAxis hide />
                  <Tooltip 
                    cursor={{fill: 'transparent'}}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="wpm" radius={[8, 8, 0, 0]} barSize={60}>
                    {speedData.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? '#cbd5e1' : i === 1 ? '#93c5fd' : '#3b82f6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-around mt-4 text-center">
              <div>
                 <div className="text-2xl font-bold text-slate-400">200</div>
                 <div className="text-xs text-slate-400 uppercase">Started WPM</div>
              </div>
              <div>
                 <div className="text-2xl font-bold text-primary">{stats.avgWpm || '~500'}</div>
                 <div className="text-xs text-primary uppercase">Current Avg WPM</div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
               <BookOpen className="w-8 h-8 text-blue-500 mb-4" />
               <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{stats.wordsRead.toLocaleString()}</div>
               <div className="text-sm text-slate-600 dark:text-slate-400">Words read across 3 free stories.</div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
               <Zap className="w-8 h-8 text-amber-500 mb-4" />
               <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{stats.maxWpm || 500}</div>
               <div className="text-sm text-slate-600 dark:text-slate-400">Peak WPM achieved.</div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center col-span-1 sm:col-span-2">
               <div className="flex items-center mb-2">
                 <Star className="w-5 h-5 text-purple-500 mr-2" />
                 <h4 className="font-bold text-slate-900 dark:text-white">Imagine what a full plan unlocks</h4>
               </div>
               <p className="text-sm text-slate-600 dark:text-slate-400">
                 Save your progress, track streaks, earn XP, and access 500+ stories. Your brain is already adapting — keep going.
               </p>
            </div>
          </div>
        </div>

        {/* Pricing Section - 3 Tiers */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Start at ${TIER_PRICING.starter}/mo — less than a coffee
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              All plans include unlimited practice sessions. Cancel anytime.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Starter Tier */}
            <div className="flex flex-col">
              <TierCard tier="starter" />
              <button 
                onClick={() => navigate('/signup')}
                className="mt-4 w-full py-3 px-6 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full transition-colors"
              >
                Get Started
              </button>
            </div>
            
            {/* Reader Tier - Best Value */}
            <div className="flex flex-col relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  Best Value
                </span>
              </div>
              <TierCard tier="reader" />
              <button 
                onClick={() => navigate('/signup')}
                className="mt-4 w-full py-3 px-6 bg-primary hover:bg-blue-600 text-white font-semibold rounded-full transition-colors"
              >
                Get Started
              </button>
            </div>
            
            {/* Ultra Tier */}
            <div className="flex flex-col">
              <TierCard tier="ultra" />
              <button 
                onClick={() => navigate('/signup')}
                className="mt-4 w-full py-3 px-6 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-full transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default UpsellPageOld;
